<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="visible" class="feedback-modal-container">
      <div class="feedback-overlay" @click="close" aria-label="关闭反馈对话框"></div>
      <div class="feedback-modal" role="dialog" aria-modal="true" aria-label="提交反馈" @keydown.esc="close">
        <div class="modal-header">
          <h3 class="modal-title"><i class="fa-solid fa-message"></i> 提交反馈</h3>
          <button class="close-btn" @click="close" aria-label="关闭">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- 反馈类型标签 -->
        <div class="tag-row" role="group" aria-label="反馈类型">
          <button v-for="tag in tags" :key="tag" class="tag-chip" :class="{ active: feedbackTag === tag }" @click="selectTag(tag)">
            {{ tag }}
          </button>
        </div>

        <!-- 字数统计与进度 -->
        <div class="char-counter">
          <span>字数：{{ charCount }}/{{ maxLen }}</span>
        </div>
        <div class="char-progress">
          <div class="char-progress-bar" :style="{ width: progressWidth }"></div>
        </div>

        <textarea
          v-model="feedbackContent"
          class="feedback-textarea"
          :maxlength="maxLen"
          placeholder="请尽可能详细描述您遇到的问题或建议（支持 Ctrl+Enter 快速提交）"
          rows="7"
          @keydown="handleTextareaKeydown"
        ></textarea>

        <ul class="tips-list" aria-label="反馈撰写提示">
          <li>包含出现问题的页面、步骤与期望行为</li>
          <li>如有报错信息或截图更佳</li>
          <li>建议类反馈请尽量说明使用场景</li>
        </ul>

        <div class="modal-actions">
          <button class="cancel-btn" @click="close">取消</button>
          <button class="clear-btn" :disabled="isSubmitting || !feedbackContent" @click="clearContent">清空</button>
          <button class="confirm-btn" :disabled="!canSubmit || isSubmitting" @click="submitFeedback">
            <i class="fa-solid fa-paper-plane"></i> {{ isSubmitting ? '提交中...' : '提交反馈' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import axios from 'axios'
import { message, loading } from '@/utils/ui-service'
import serverUrl from '@/assets/BaseServerUrl'
import authService from '@/services/authService'
import eventBus from '@/utils/eventBus'

export default {
  name: 'FeedbackModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      feedbackContent: '',
      isSubmitting: false,
      tags: ['Bug', '建议', '体验', '其他'],
      feedbackTag: null,
      maxLen: 500
    }
  },
  computed: {
    charCount() {
      return (this.feedbackContent || '').trim().length
    },
    canSubmit() {
      return this.feedbackContent && this.feedbackContent.trim().length >= 5 && this.charCount <= this.maxLen
    },
    progressWidth() {
      const pct = Math.min(100, Math.round((this.charCount / this.maxLen) * 100))
      return pct + '%'
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    selectTag(tag) {
      this.feedbackTag = tag
    },
    clearContent() {
      this.feedbackContent = ''
    },
    handleTextareaKeydown(e) {
      if (e.ctrlKey && e.key === 'Enter' && this.canSubmit && !this.isSubmitting) {
        this.submitFeedback()
      }
    },
    async submitFeedback() {
      if (this.isSubmitting) return
      if (!this.canSubmit) {
        message.warning('请输入不少于5个字符的反馈内容')
        return
      }
      if (!authService.isLoggedIn()) {
        message.warning('请先登录后再提交反馈')
        eventBus.emit('show-login')
        return
      }

      const user = authService.getCurrentUser()
      const token = localStorage.getItem('userToken')

      this.isSubmitting = true
      
      const loadingInstance = loading.service({
        lock: true,
        text: '正在提交并进行AI内容审核，请稍候...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'ai-loading-overlay'
      })

      try {
        const content = this.feedbackTag
          ? `[${this.feedbackTag}] ${this.feedbackContent.trim()}`
          : this.feedbackContent.trim()

        const payload = {
          userId: user?.userId,
          username: user?.username || '匿名用户',
          content
        }

        const response = await axios.post(`${serverUrl}/api/feedback`, payload, {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        })

        const data = response?.data || {}
        if (data.success) {
          if (data.emailSent === false) {
            message.warning(data.message || '反馈已保存，但邮件发送失败')
          } else {
            message.success(data.message || '反馈提交成功，感谢您的支持！')
          }
          this.feedbackContent = ''
          this.feedbackTag = null
          this.close()
        } else {
          const errMsg = data.error || data.message || '提交失败，请稍后重试'
          throw new Error(errMsg)
        }
      } catch (error) {
        console.error('提交反馈失败:', error)
        const status = error?.response?.status
        const serverMsg = error?.response?.data?.error || error?.response?.data?.message
        if (status === 401) {
          message.error(serverMsg || '登录状态异常或令牌过期，请重新登录')
          eventBus.emit('show-login')
        } else if (status === 429) {
          message.warning(serverMsg || '24小时内仅可提交一次反馈')
        } else {
          message.error(serverMsg || error.message || '提交失败，请稍后重试')
        }
      } finally {
        this.isSubmitting = false
        loadingInstance.close()
      }
    }
  }
}
</script>

<style scoped>
.feedback-modal-container { position: fixed; inset: 0; z-index: var(--z-modal-elevated); }
.feedback-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.45); }
.feedback-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 92%;
  max-width: 620px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(0,0,0,0.24);
  padding: 14px 16px;

}

.dark-theme .feedback-modal { background: #1e1e1e; color: #eaeaea; }
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.modal-title { margin: 0; font-size: 1.2em; font-family: '爱点风雅黑'; }
.close-btn { background: transparent; border: none; font-size: 1.2em; cursor: pointer; color: #888; }
.dark-theme .close-btn { color: #ccc; }

.tag-row { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
.tag-chip { padding: 6px 10px; border: 1px solid #d0d7ff; border-radius: 999px; background: #f5f7ff; cursor: pointer; font-size: 0.88em; transition: all 0.3s; }
.tag-chip:hover { filter: brightness(1.02); }
.tag-chip.active { background: #1a73e8; border-color: #1a73e8; color: #fff; }
.dark-theme .tag-chip { background: #222c3a; border-color: #2f3c4f; color: #cdd6f4; }
.dark-theme .tag-chip.active { background: #7aa2f7; border-color: #7aa2f7; color: #111; }

.char-counter { text-align: right; font-size: 0.9em; color: #666; }
.dark-theme .char-counter { color: #c8c8c8; }
.char-progress { height: 6px; background: #eee; border-radius: 999px; overflow: hidden; margin: 8px 0 10px; }
.dark-theme .char-progress { background: #333; }
.char-progress-bar { height: 100%; background: linear-gradient(90deg, #1a73e8, #6c42f5); border-radius: 999px; width: 0; transition: width 0.2s ease; }

.feedback-textarea {
  width: calc(100% - 20px);
  resize: vertical;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 0.95em;
  min-height: 140px;
  background: white;
  color: #333;
}
.feedback-textarea::placeholder { font-family: '猫啃网糖圆体'; }
.dark-theme .feedback-textarea { background: #2a2a2a; border-color: #444; color: #eaeaea; }

.tips-list { margin: 10px 0 0; padding-left: 18px; color: #666; font-size: 0.92em; }
.dark-theme .tips-list { color: #c8c8c8; }

.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.cancel-btn, .confirm-btn, .clear-btn { padding: 8px 14px; border-radius: 10px; border: none; cursor: pointer; transition: all 0.3s; }
.cancel-btn { background: #f3f3f3; color: #333; }
.clear-btn { background: #fee2e2; color: #b91c1c; }
.confirm-btn { background: #1a73e8; color: #fff; }
.confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

@media (max-width: 480px) {
  .feedback-modal { 
      inset: 0; 
      left: 0; 
      top: 0; 
      transform: none; 
      width: calc(100% - 32px); 
      max-width: none; 
      height: calc(100% - 32px); 
      max-height: none; 
      border-radius: 0; 
      padding: 16px; 
      overflow-y: auto; 
      display: flex; 
      flex-direction: column; 
  }
  .feedback-textarea {
      flex: 1; /* 让文本域在移动端填满剩余空间 */
      min-height: 100px;
  }
  .modal-actions { 
      margin-top: auto; 
  }
  .tips-list{
    margin-bottom: 16px;
  }
}
</style>
